import { monthIndexToMonth } from 'api/recommendations'
import moment from 'moment'
import type { IntlShape } from 'react-intl'

interface Props {
  checkIn?: string
  monthIndex?: string
  duration: number
  intl: IntlShape
}

export function getRecommendationsTitle({
  checkIn,
  monthIndex,
  duration,
  intl,
}: Props): { title: string, subtitle?: string } {
  if (checkIn) {
    const displayFormat = 'D MMM'
    const from = moment(checkIn).format(displayFormat)
    const to = moment(checkIn).add(duration, 'days').format(displayFormat)
    const title = intl.formatMessage({
      id: '0xsbWhF4Y/',
      defaultMessage: 'Best offers for your dates',
      description: 'Calendar recommendations title when specific check-in date is selected',
    })
    const subtitle = intl.formatMessage(
      {
        id: 'A3+jaw2Dfe',
        defaultMessage: '{from} - {to} ({duration, plural, one {# night} other {# nights}})',
        description: 'Calendar recommendations subtitle showing date range and duration',
      },
      { from, to, duration },
    )
    return {
      title,
      subtitle,
    }
  }
  if (monthIndex) {
    return {
      title: intl.formatMessage(
        {
          id: 'UIwl8apHWA',
          defaultMessage: 'Check our best selling hotels available in {month}',
          description: 'Calendar recommendations title when a month is selected',
        },
        { month: moment(monthIndexToMonth(monthIndex), 'M-YYYY').format('MMMM') },
      ),
    }
  }
  return {
    title: intl.formatMessage({
      id: '5IWzyj1mov',
      defaultMessage: 'Check our best selling hotels',
      description: 'Default calendar recommendations title',
    }),
  }
}

interface MobileProps {
  checkIn?: string
  monthIndex?: string
  duration: number
  intl: IntlShape
  variant?: 'available' | 'default'
}

export function getRecommendationsTitleMobile({
  checkIn,
  monthIndex,
  duration,
  intl,
  variant = 'default',
}: MobileProps): string {
  if (checkIn) {
    const displayFormat = 'DD MMM'
    const from = moment(checkIn).format(displayFormat)
    const to = moment(checkIn).add(duration, 'days').format(displayFormat)
    if (variant === 'available') {
      return intl.formatMessage(
        {
          id: 'PoL1+yNNvV',
          defaultMessage: 'Available for {from} - {to}',
          description: 'Mobile calendar recommendations title showing availability date range',
        },
        { from, to },
      )
    }
    return intl.formatMessage(
      {
        id: 'K843VOiDeN',
        defaultMessage: '{from} - {to}',
        description: 'Mobile calendar recommendations title showing date range',
      },
      { from, to },
    )
  }
  if (monthIndex) {
    const month = moment(monthIndexToMonth(monthIndex), 'M-YYYY').format('MMMM')
    if (variant === 'available') {
      return intl.formatMessage(
        {
          id: '+sZV8lQkpV',
          defaultMessage: 'Available in {month}',
          description: 'Mobile calendar recommendations title showing availability month',
        },
        { month },
      )
    }
    return intl.formatMessage(
      {
        id: 'Lw+BL9NMlT',
        defaultMessage: 'Check our best selling hotels available in {month}',
        description: 'Mobile calendar recommendations title when a month is selected',
      },
      { month },
    )
  }
  if (variant === 'available') {
    return intl.formatMessage({
      id: '1pxp3IiQxM',
      defaultMessage: 'Similar hotels',
      description: 'Default mobile recommendations title for similar hotels',
    })
  }
  return intl.formatMessage({
    id: '0xduaOVe1/',
    defaultMessage: 'Check our best selling hotels',
    description: 'Default mobile calendar recommendations title',
  })
}
